
import SocialFeedItemsView from '@/components/social/SocialFeedItemsView.vue';
import SocialFeedPagination from '@/components/social/SocialFeedPagination.vue';
import SocialFeedPostDetailModal from '@/components/social/SocialFeedPostDetailModal.vue';
import { GENERIC_ERROR_DESCRIPTION, SOCIAL_PLATFORMS_METADATA } from '@/constants';
import { BotMixin } from '@/mixins/BotMixin';
import { SocialFeedItem, SocialFeedPlatform } from '@/types/socialFeed';
import {
  clampSocialFeedPage,
  fetchSocialFeedItems,
  paginateSocialFeedItems,
  PROJECT_SOCIAL_FEED_PAGE_SIZE,
  socialFeedPlatformLabel,
} from '@/utils/socialFeed';
import { Bot, SupportedPlatform } from 'ignite360-core';
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';

@Component({
  name: 'platform-latest-posts-card',
  components: { SocialFeedItemsView, SocialFeedPagination, SocialFeedPostDetailModal },
})
export default class PlatformLatestPostsCard extends mixins(BotMixin) {
  readonly pageSize = PROJECT_SOCIAL_FEED_PAGE_SIZE;

  @Prop({ required: true, type: String })
  readonly platform!: SocialFeedPlatform;

  items: SocialFeedItem[] = [];
  currentPage = 1;
  isLoading = false;
  isRefreshing = false;
  loadError: string | null = null;
  showDetailModal = false;
  selectedItem: SocialFeedItem | null = null;

  get platformMeta() {
    return SOCIAL_PLATFORMS_METADATA.find((meta) => meta.value === this.platform);
  }

  get isConnected(): boolean {
    return !!this.platformMeta?.isConnected?.(this.bot);
  }

  get isPostingDisabled(): boolean {
    return !!this.platformMeta?.isDisabled?.(this.bot);
  }

  get platformLabel(): string {
    return this.platformMeta?.label || socialFeedPlatformLabel(this.platform);
  }

  get platformImage(): string {
    return this.platformMeta?.image || '';
  }

  get accountLabel(): string {
    return this.resolveAccountLabel(this.bot);
  }

  get paginatedItems(): SocialFeedItem[] {
    return paginateSocialFeedItems(this.items, this.currentPage, this.pageSize);
  }

  @Watch('bot', { immediate: true, deep: true })
  async onBotChange() {
    if (!this.isConnected) {
      this.items = [];
      this.currentPage = 1;
      this.loadError = null;
      return;
    }
    await this.loadPosts();
  }

  async loadPosts(force = false) {
    if (!this.isConnected) {
      return;
    }
    if (!force && this.items.length) {
      return;
    }
    if (force) {
      this.isRefreshing = true;
    } else {
      this.isLoading = true;
    }
    this.loadError = null;
    try {
      this.items = await fetchSocialFeedItems(this.bot.id, this.platform);
      this.currentPage = 1;
    } catch (e) {
      this.loadError = GENERIC_ERROR_DESCRIPTION;
      this.items = [];
    } finally {
      if (force) {
        this.isRefreshing = false;
      } else {
        this.isLoading = false;
      }
    }
  }

  onOpenPost(item: SocialFeedItem) {
    this.selectedItem = item;
    this.showDetailModal = true;
  }

  onPostDeleted(item: SocialFeedItem) {
    this.items = this.items.filter(
      (existing) => !(existing.id === item.id && existing.platform === item.platform),
    );
    this.selectedItem = null;
    this.syncCurrentPage();
  }

  goToPreviousPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage - 1, this.items.length, this.pageSize);
  }

  goToNextPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage + 1, this.items.length, this.pageSize);
  }

  syncCurrentPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage, this.items.length, this.pageSize);
  }

  private resolveAccountLabel(bot: Bot): string {
    if (this.platform === SupportedPlatform.Instagram) {
      return bot.platformData.instagram.pageName || '';
    }
    if (this.platform === SupportedPlatform.FacebookMessenger) {
      return bot.platformData.facebookMessenger.pageName || '';
    }
    if (this.platform === SupportedPlatform.LinkedIn) {
      const type = bot.platformData.linkedIn.type;
      return type ? `Connected as ${type}` : '';
    }
    if (this.platform === SupportedPlatform.GoogleBusiness) {
      return bot.platformData.googleBusiness.locationId
        ? `Location ${bot.platformData.googleBusiness.locationId}`
        : '';
    }
    return '';
  }
}
